import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";
import ServiceStore from "../../../stores/ServiceStore";
import {
  Opportunity,
  OpportunityListFilters,
} from "../types/Opportunity.types";

export class OpportunitiesListStore {
  rootStore: RootStore;

  // Observable state
  opportunities: Opportunity[] = [];
  filters: OpportunityListFilters = {};
  searchQuery = "";
  loading = false;
  error: string | null = null;
  totalCount = 0;
  currentPage = 1;
  pageSize = 50;
  filtersVisible = true;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // Service store access
  get opportunitiesStore(): ServiceStore {
    return this.rootStore.opportunitiesStore as ServiceStore;
  }

  get customersStore(): ServiceStore {
    return this.rootStore.customersStore as ServiceStore;
  }

  get contactsStore(): ServiceStore {
    return this.rootStore.contactsStore as ServiceStore;
  }

  // Data loading methods
  async loadOpportunities(newFilters: OpportunityListFilters = {}) {
    this.loading = true;
    this.error = null;

    try {
      // First load all base entities from ServiceStores to ensure relational data is available
      await Promise.all([
        // Load all base entities without queries to populate stores
        this.opportunitiesStore.find(),
        this.customersStore.find(),
        this.contactsStore.find(),
      ]);

      // Merge new filters with existing ones
      this.filters = { ...this.filters, ...newFilters };

      // Build query
      const query: Record<string, unknown> = {
        $sort: { EstimatedValue: -1 },
        $limit: this.pageSize,
        $skip: (this.currentPage - 1) * this.pageSize,
      };

      // Apply filters
      if (this.filters.salesStage) {
        query.salesStage = this.filters.salesStage;
      }

      if (this.filters.opportunityType) {
        query.OpportunityType = this.filters.opportunityType;
      }

      if (this.filters.leadSource) {
        query.LeadSource = this.filters.leadSource;
      }

      if (this.filters.accountId) {
        query.accountId = this.filters.accountId;
      }

      if (this.filters.winProbabilityMin !== undefined) {
        query.winProbability = { $gte: this.filters.winProbabilityMin };
      }

      if (this.filters.estimatedValueMin !== undefined) {
        query.estimatedValue = { $gte: this.filters.estimatedValueMin };
      }

      if (this.filters.estimatedValueMax !== undefined) {
        query.estimatedValue = {
          ...(query.estimatedValue as object),
          $lte: this.filters.estimatedValueMax,
        };
      }

      if (this.filters.expectedCloseDateFrom) {
        query.expectedCloseDate = { $gte: this.filters.expectedCloseDateFrom };
      }

      if (this.filters.expectedCloseDateTo) {
        query.expectedCloseDate = {
          ...(query.expectedCloseDate as object),
          $lte: this.filters.expectedCloseDateTo,
        };
      }

      // Apply search query
      if (this.searchQuery) {
        query.$or = [
          { OpportunityName: { $regex: this.searchQuery, $options: "i" } },
          { Description: { $regex: this.searchQuery, $options: "i" } },
        ];
      }

      const response = await this.opportunitiesStore.find({ query });
      runInAction(() => {
        this.opportunities = response?.data as unknown as Opportunity[];
        this.totalCount = response?.total || 0;
      });
    } catch (error) {
      this.error =
        error instanceof Error ? error.message : "Failed to load opportunities";
      console.error("Failed to load opportunities:", error);
    } finally {
      this.loading = false;
    }
  }

  // Pagination methods
  async goToPage(page: number) {
    this.currentPage = page;
    await this.loadOpportunities();
  }

  async nextPage() {
    if (this.hasNextPage) {
      await this.goToPage(this.currentPage + 1);
    }
  }

  async previousPage() {
    if (this.hasPreviousPage) {
      await this.goToPage(this.currentPage - 1);
    }
  }

  // Filter management methods
  setSearchQuery(query: string) {
    this.searchQuery = query;
    this.currentPage = 1; // Reset to first page when searching
    this.loadOpportunities();
  }

  updateFilters(newFilters: OpportunityListFilters) {
    this.filters = newFilters;
    this.currentPage = 1; // Reset to first page when filtering
    this.loadOpportunities();
  }

  clearFilters() {
    this.filters = {};
    this.searchQuery = "";
    this.currentPage = 1;
    this.loadOpportunities();
  }

  toggleFiltersVisibility() {
    this.filtersVisible = !this.filtersVisible;
  }

  // Opportunity management methods
  async createOpportunity(opportunityData: Partial<Opportunity>) {
    try {
      const opportunity = (await this.opportunitiesStore.create({
        ...opportunityData,
        CreatedAt: new Date().toISOString(),
        UpdatedAt: new Date().toISOString(),
      })) as unknown as Opportunity;

      // Add to beginning of list if on first page
      if (this.currentPage === 1) {
        this.opportunities.unshift(opportunity);
      }

      this.totalCount++;
      return opportunity;
    } catch (error) {
      console.error("Failed to create opportunity:", error);
      throw error;
    }
  }

  async updateOpportunity(
    opportunityId: number,
    updates: Partial<Opportunity>
  ) {
    try {
      const updated = (await this.opportunitiesStore.patch(opportunityId, {
        ...updates,
        UpdatedAt: new Date().toISOString(),
      })) as unknown as Opportunity;

      // Update in local list
      const index = this.opportunities.findIndex(
        (o: any) => o.opportunityId === opportunityId
      );
      if (index !== -1) {
        this.opportunities[index] = {
          ...this.opportunities[index],
          ...updated,
        };
      }

      return updated;
    } catch (error) {
      console.error("Failed to update opportunity:", error);
      throw error;
    }
  }

  async deleteOpportunity(opportunityId: number) {
    try {
      await this.opportunitiesStore.remove(opportunityId);

      // Remove from local list
      this.opportunities = this.opportunities.filter(
        (o: any) => o.opportunityId !== opportunityId
      );
      this.totalCount--;
    } catch (error) {
      console.error("Failed to delete opportunity:", error);
      throw error;
    }
  }

  // Quick actions
  async searchOpportunities(query: string) {
    this.setSearchQuery(query);
    this.currentPage = 1; // Reset to first page
    await this.loadOpportunities();
  }

  async applyFilters(filters: OpportunityListFilters) {
    this.updateFilters(filters);
    await this.loadOpportunities();
  }

  async clearAndReload() {
    this.clearFilters();
    this.currentPage = 1;
    await this.loadOpportunities();
  }

  // Computed getters
  get hasFilters(): boolean {
    return Object.keys(this.filters).length > 0 || this.searchQuery.length > 0;
  }

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  get hasNextPage(): boolean {
    return this.currentPage < this.totalPages;
  }

  get hasPreviousPage(): boolean {
    return this.currentPage > 1;
  }

  get displayRange(): string {
    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalCount);
    return `${start}-${end} of ${this.totalCount}`;
  }

  get totalOpportunityValue(): number {
    return this.opportunities.reduce(
      (sum, opp) => sum + (opp.estimatedValue || 0),
      0
    );
  }

  get averageWinProbability(): number {
    if (this.opportunities.length === 0) return 0;
    const total = this.opportunities.reduce(
      (sum, opp) => sum + (opp.winProbability || 0),
      0
    );
    return total / this.opportunities.length;
  }

  get opportunitiesByStage(): Record<string, Opportunity[]> {
    return this.opportunities.reduce((acc, opp) => {
      const stage = opp.salesStage || "Unknown";
      if (!acc[stage]) {
        acc[stage] = [];
      }
      acc[stage].push(opp);
      return acc;
    }, {} as Record<string, Opportunity[]>);
  }
}

export default OpportunitiesListStore;
